import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './DashboardDisplay.module.scss';

// Mock Chart Component (en producción usarías Chart.js, D3.js, etc.)
const MockChart = ({ type, data, title }) => {
  const renderChart = () => {
    switch (type) {
      case 'donut': {
        const total =
          Number(data.total) ||
          (Array.isArray(data.segments)
            ? data.segments.reduce((s, seg) => s + Number(seg.value || 0), 0)
            : 0);
        let startDeg = 0;
        const gradientParts = Array.isArray(data.segments)
          ? data.segments.map(segment => {
              const percent = total > 0 ? Number(segment.value || 0) / total : 0;
              const endDeg = startDeg + percent * 360;
              const part = `${segment.color || '#2185d0'} ${startDeg}deg ${endDeg}deg`;
              startDeg = endDeg;
              return part;
            })
          : [];
        const gradient = `conic-gradient(${gradientParts.join(', ')})`;
        const showLegend = data.showLegend !== false;

        return (
          <div className={styles.donutChart}>
            <div className={styles.donutInner} style={{ background: gradient }}>
              <div className={styles.donutValue}>{data.total}</div>
              <div className={styles.donutLabel}>Total</div>
            </div>
            {showLegend && (
              <div className={styles.donutSegments}>
                {data.segments.map((segment, index) => (
                  <div
                    key={index}
                    className={styles.donutSegment}
                    style={{
                      '--segment-color': segment.color,
                      '--segment-percentage': `${(segment.value / (total || 1)) * 100}%`,
                    }}
                  >
                    <span className={styles.segmentValue}>{segment.value}</span>
                    <span className={styles.segmentLabel}>{segment.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
      case 'bar':
        return (
          <div className={styles.barChart}>
            {(data.bars || data.values)?.map((bar, index) => {
              // Support both old format (data.bars) and new format (data.values + data.labels)
              const value = data.bars ? bar.value : bar;
              const label = data.bars
                ? bar.label
                : data.labels
                ? data.labels[index]
                : `Item ${index + 1}`;
              const color = data.bars ? bar.color : data.colors ? data.colors[index] : '#2185d0';
              const maxValue = data.bars
                ? Math.max(...data.bars.map(b => b.value))
                : Math.max(...data.values);

              return (
                <div key={index} className={styles.barContainer}>
                  <div
                    className={styles.bar}
                    style={{
                      height: `${(value / maxValue) * 100}%`,
                      backgroundColor: color,
                    }}
                  />
                  <div className={styles.barLabel}>{label}</div>
                  <div className={styles.barValue}>{value}</div>
                </div>
              );
            })}
          </div>
        );
      case 'line':
        return (
          <div className={styles.lineChart}>
            <svg viewBox="0 0 300 150" className={styles.lineSvg}>
              {data.points && data.points.length > 1 && (
                <polyline
                  fill="none"
                  stroke={data.color || '#2185d0'}
                  strokeWidth="3"
                  points={data.points
                    .map(
                      (point, index) =>
                        `${(index / (data.points.length - 1)) * 280 + 10},${
                          140 - (point / Math.max(...data.points)) * 120
                        }`
                    )
                    .join(' ')}
                />
              )}
              {data.points &&
                data.points.map((point, index) => (
                  <circle
                    key={index}
                    cx={(index / (data.points.length - 1)) * 280 + 10}
                    cy={140 - (point / Math.max(...data.points)) * 120}
                    r="4"
                    fill={data.color || '#2185d0'}
                  />
                ))}
            </svg>
            <div className={styles.lineLabels}>
              {data.labels &&
                data.labels.map((label, index) => (
                  <span key={index} className={styles.lineLabel}>
                    {label}
                  </span>
                ))}
            </div>
          </div>
        );
      default:
        return <div className={styles.defaultChart}>Chart: {type}</div>;
    }
  };

  return (
    <div className={styles.chartContainer}>
      {title && <h4 className={styles.chartTitle}>{title}</h4>}
      {renderChart()}
    </div>
  );
};

const MetricCard = ({ title, value, subtitle, status, icon }) => (
  <div className={classNames(styles.metricCard, styles[`status-${status}`])}>
    {icon && <div className={styles.metricIcon}>📊</div>}
    <div className={styles.metricContent}>
      <div className={styles.metricTitle}>{title}</div>
      <div className={styles.metricValue}>{value}</div>
      {subtitle && <div className={styles.metricSubtitle}>{subtitle}</div>}
    </div>
  </div>
);

const AlertCard = ({ type, message, action, priority }) => (
  <div
    className={classNames(
      styles.alertCard,
      styles[`alert-${type}`],
      styles[`priority-${priority}`]
    )}
  >
    <div className={styles.alertIcon}>
      {type === 'critical' ? '🚨' : type === 'warning' ? '⚠️' : 'ℹ️'}
    </div>
    <div className={styles.alertContent}>
      <div className={styles.alertMessage}>{message}</div>
      {action && <div className={styles.alertAction}>{action}</div>}
    </div>
  </div>
);

const ImageReport = ({ src, title, description, status }) => {
  const [isApproved, setIsApproved] = React.useState(false);

  const handleApprovalToggle = () => {
    setIsApproved(!isApproved);
  };

  return (
    <div className={classNames(styles.imageReport, styles[`status-${status}`])}>
      <div className={styles.imageContainer}>
        <img src={src} alt={title} className={styles.reportImage} />
        <div className={styles.imageOverlay}>
          <button
            className={classNames(styles.approvalButton, {
              [styles.approved]: isApproved,
              [styles.pending]: !isApproved,
            })}
            onClick={handleApprovalToggle}
          >
            {isApproved ? (
              <>
                <span className={styles.checkIcon}>✓</span>
                Approved
              </>
            ) : (
              'Approve'
            )}
          </button>
        </div>
      </div>
      <div className={styles.imageContent}>
        <h4 className={styles.imageTitle}>{title}</h4>
        <p className={styles.imageDescription}>{description}</p>
      </div>
    </div>
  );
};

const DashboardDisplay = ({ dashboardData, className }) => {
  console.log('DashboardDisplay dashboardData:', dashboardData);

  const renderContent = useMemo(() => {
    if (!dashboardData) return null;

    const { type, title, data } = dashboardData;

    switch (type) {
      case 'metrics':
        return (
          <div className={styles.metricsContainer}>
            <div className={styles.metricsGrid}>
              {data.metrics &&
                data.metrics.map((metric, index) => <MetricCard key={index} {...metric} />)}
            </div>
            {data.charts &&
              data.charts.map((chart, index) => (
                <div key={index} className={styles.chartWrapper}>
                  <MockChart {...chart} />
                </div>
              ))}
            {data.alerts && data.alerts.length > 0 && (
              <div className={styles.alertsSection}>
                <h4 className={styles.alertsTitle}>Alerts & Actions</h4>
                <div className={styles.alertsGrid}>
                  {data.alerts.map((alert, index) => (
                    <AlertCard key={index} {...alert} />
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'image_report':
        return (
          <div className={styles.imageReportContainer}>
            <ImageReport {...data} />
            {data.additional_info && (
              <div className={styles.additionalInfo}>
                <h4>Additional Information</h4>
                <ul>
                  {data.additional_info.map((info, index) => (
                    <li key={index}>{info}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );

      case 'performance':
        return (
          <div className={styles.performanceDashboard}>
            <div className={styles.performanceHeader}>
              <h3>{title}</h3>
            </div>
            <div className={styles.performanceGrid}>
              {data.kpis && data.kpis.map((kpi, index) => <MetricCard key={index} {...kpi} />)}
              {data.charts &&
                data.charts.map((chart, index) => (
                  <div key={index} className={styles.performanceChart}>
                    <MockChart {...chart} />
                  </div>
                ))}
            </div>
          </div>
        );

      default:
        return (
          <div className={styles.defaultDashboard}>
            <h3>{title || 'Dashboard'}</h3>
            <p>Dashboard type: {type}</p>
            <pre>{JSON.stringify(data, null, 2)}</pre>
          </div>
        );
    }
  }, [dashboardData]);

  if (!dashboardData) {
    return (
      <div className={classNames(styles.wrapper, className)}>
        <div className={styles.noDashboard}>
          <p>No dashboard data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.wrapper, className)}>
      <div className={styles.dashboard}>{renderContent}</div>
    </div>
  );
};

DashboardDisplay.propTypes = {
  dashboardData: PropTypes.shape({
    type: PropTypes.string.isRequired,
    title: PropTypes.string,
    data: PropTypes.object.isRequired,
  }),
  className: PropTypes.string,
};

DashboardDisplay.defaultProps = {
  dashboardData: null,
  className: '',
};

MockChart.propTypes = {
  type: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  title: PropTypes.string,
};

MockChart.defaultProps = {
  title: '',
};

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string,
  status: PropTypes.string,
  icon: PropTypes.string,
};

MetricCard.defaultProps = {
  subtitle: '',
  status: 'neutral',
  icon: '',
};

ImageReport.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  status: PropTypes.string,
};

ImageReport.defaultProps = {
  status: 'neutral',
};

export default DashboardDisplay;
